import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Loader2, Server } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import RegionSelect from '@/components/common/RegionSelect';
import { useCurrencyRegions } from '@/hooks/useCurrencyRegions';
import { defaultRegion } from '@/lib/regions';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';
import { useLicensing } from '@/hooks/useLicensing';
import {
  CatalogData,
  hetznerCost,
  margin,
  PriceCell,
  priceCell,
  ServerConfig,
  today,
} from '@/lib/pricing';
import SetPriceDialog, { PriceTarget } from './SetPriceDialog';

const day = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });

export default function CloudPriceList() {
  const regions = useCurrencyRegions();
  const [region, setRegion] = React.useState('');
  const [target, setTarget] = React.useState<PriceTarget | null>(null);

  React.useEffect(() => {
    if (region) return;
    const list = regions.data?.regions ?? [];
    const start = defaultRegion(list);
    if (start) setRegion(start.slug);
  }, [region, regions.data]);

  const configs = useQuery<ServerConfig[]>({
    queryKey: ['hetzner-configs'],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return (await api.get('/global-admin/hetzner-configs/')).data;
    },
  });

  const catalog = useQuery<CatalogData>({
    queryKey: ['hetzner-catalog'],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return (await api.get('/global-admin/hetzner-catalog/')).data;
    },
  });

  const license = useLicensing();
  const tiers = license.data?.tiers ?? [];

  const serverTypes = React.useMemo(() => {
    const map = new Map<string, CatalogData['server_types'][number]>();
    catalog.data?.server_types.forEach((type) => map.set(type.name, type));
    return map;
  }, [catalog.data]);

  const costMissing = (catalog.error as any)?.response?.status === 503;

  const rows = React.useMemo(
    () => [...(configs.data ?? [])].sort((a, b) => a.sort_order - b.sort_order
      || a.display_name.localeCompare(b.display_name)),
    [configs.data],
  );


  const todayIso = today();

  const cellBody = (config: ServerConfig, tierSlug: string, cost: number | null) => {
    const cell: PriceCell = priceCell(config.prices, tierSlug, region);
    if (cell.state === 'none') {
      return <span className="text-sm text-muted-foreground">Set a price</span>;
    }
    const row = cell.row;
    const shown = margin({ currency: row.currency, amount: row.monthly_price }, cost);
    const inherited = cell.state === 'inherited';
    return (
      <div className={inherited ? 'text-muted-foreground' : ''}>
        <div className="font-mono text-sm font-medium" data-testid={`price-amount-${config.hetzner_server_type}-${tierSlug}`}>
          {Number(row.monthly_price).toLocaleString()} {row.currency}
        </div>
        {inherited ? (
          <div className="text-xs" data-testid={`price-inherited-${config.hetzner_server_type}-${tierSlug}`}>
            = standard price
          </div>
        ) : (
          <div className="text-xs" data-testid={`price-margin-${config.hetzner_server_type}-${tierSlug}`}>
            {shown ? (
              <span className={shown.amount < 0 ? 'text-destructive' : 'text-emerald-600'}>
                {shown.amount >= 0 ? '+' : ''}{shown.amount.toFixed(2)} ({shown.percent.toFixed(0)}%)
              </span>
            ) : (
              <span className="text-muted-foreground">{cost == null ? '—' : 'margin n/a'}</span>
            )}
          </div>
        )}
        {cell.state === 'set' && cell.row.effective_date > todayIso ? (
          <div className="text-xs text-amber-600">not yet in force, from {day(cell.row.effective_date)}</div>
        ) : cell.state === 'set' && cell.upcoming ? (
          <div className="text-xs text-amber-600">
            {Number(cell.upcoming.monthly_price).toLocaleString()} from {day(cell.upcoming.effective_date)}
          </div>
        ) : null}
      </div>
    );
  };

  const liveTarget = React.useMemo(() => {
    if (!target) return null;
    const config = rows.find((row) => row.id === target.configId);
    return config ? { ...target, prices: config.prices } : target;
  }, [target, rows]);

  return (
    <Card data-testid="cloud-price-list">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Server className="h-4 w-4" />
              Cloud servers
            </CardTitle>
            <CardDescription>
              What each tier pays a month for a server we run for them, next to
              what it costs us. A tier with no price of its own is charged the
              small business price. A negotiated plan overrides both, in its own
              card below. Click any price to change it or read its history.
            </CardDescription>
          </div>
          <div className="w-64">
            <RegionSelect
              value={region}
              onChange={setRegion}
              regions={regions.data?.regions ?? []}
              testId="price-list-region"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {costMissing && (
          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <span>
              No Hetzner API key is configured, so what a server costs us is
              unknown and no margin can be shown. Prices can still be set.
            </span>
          </div>
        )}

        {configs.isLoading || license.isLoading ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            No server sizes are offered yet. Add one on the Server Configs page.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="price-list">
              <TableHeader>
                <TableRow>
                  <TableHead>Size</TableHead>
                  <TableHead>Our cost</TableHead>
                  {tiers.map((tier) => (
                    <TableHead key={tier.slug}>{tier.name}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((config) => {
                  const cost = hetznerCost(serverTypes.get(config.hetzner_server_type), config.hetzner_location);
                  const unpriced = tiers.every((tier) => priceCell(config.prices, tier.slug, region).state === 'none');
                  return (
                    <TableRow key={config.id} data-testid={`price-row-${config.hetzner_server_type}`}
                      className={config.is_active ? '' : 'opacity-60'}>
                      <TableCell>
                        <div className="font-medium">{config.display_name}</div>
                        <div className="font-mono text-xs text-muted-foreground">{config.hetzner_server_type}</div>
                        <div className="text-xs text-muted-foreground">
                          {config.vcpus} vCPU · {config.memory_gb} GB · {config.disk_gb} GB
                          {config.is_active ? '' : ' · not offered'}
                        </div>
                        {unpriced && (
                          <div className="mt-1 flex items-center gap-1 text-xs text-destructive"
                            data-testid={`price-missing-${config.hetzner_server_type}`}>
                            <AlertTriangle className="h-3 w-3" />
                            No price, so it cannot be invoiced
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-sm" data-testid={`price-cost-${config.hetzner_server_type}`}>
                        {cost == null ? <span className="text-muted-foreground">—</span> : `${cost.toFixed(2)} EUR`}
                      </TableCell>
                      {tiers.map((tier) => (
                        <TableCell key={tier.slug} className="p-0">
                          <button
                            type="button"
                            className="h-full w-full rounded px-4 py-2 text-left hover:bg-muted/60"
                            data-testid={`price-cell-${config.hetzner_server_type}-${tier.slug}`}
                            onClick={() => setTarget({
                              configId: config.id,
                              configName: config.display_name,
                              scope: { tier: tier.slug },
                              scopeKey: tier.slug,
                              scopeLabel: tier.name,
                              prices: config.prices,
                              cost,
                            })}
                          >
                            {cellBody(config, tier.slug, cost)}
                          </button>
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <SetPriceDialog target={liveTarget} defaultRegion={region} onClose={() => setTarget(null)} />
    </Card>
  );
}
